// DynamicShiftPage.jsx
import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography, Popover, Button, TextField , CircularProgress} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import itLocale from 'date-fns/locale/it';
import { withTranslation } from 'react-i18next';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Print as PrintIcon } from '@mui/icons-material';
import MonthlyShift from './MonthlyShift';
import { cicassService, auth } from '../../../_services';

const apiUrl = "dynamicshift";
const DynamicShiftPage = ({ t, history, location, match }) => {
  // Estraggo l'ID del calendario dinamico dal routing (es. id 6)
  const id = match?.params?.id;
  const calendarIdFromRoute = id ? parseInt(id, 10) : -1;

  // Recuperiamo l'eventuale stato di navigazione precedente (es. ritorno da un dettaglio)
  const [selectedDate, setSelectedDate] = useState(() => {
    const state = location.state;
    if (state && state.targetYear !== undefined) {
      return new Date(state.targetYear, state.targetMonth, 1);
    }
    return new Date();
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [targetEventId, setTargetEventId] = useState(location.state?.targetEventId);
  const [calendarName, setCalendarName] = useState("");
  // Gestione Stampa turni:
  const [anchorEl, setAnchorEl] = useState(null);
  const [printDate, setPrintDate] = useState(new Date());
  const handleOpenPrintMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClosePrintMenu = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  // Limiti temporali di navigazione (es. da quest'anno dalla attivazione del servizio anni fa a +2 anni rispetto ad oggi)
  const minDate = new Date(2026, 4, 1);
  const maxDate = new Date(new Date().getFullYear() + 1, 11, 31);
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();
  const isPrevDisabled = year <= minDate.getFullYear() && month <= minDate.getMonth();
  const isNextDisabled = year >= maxDate.getFullYear() && month >= maxDate.getMonth(); 
  
  const clearTargetEvent = () => setTargetEventId(null);

  useEffect(() => {
    setIsReady(true);
  }, []);

  const handleExportExcel = async (calendarId, date) => {
    const yyyymmdd = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
    setIsDownloading(true);
    try {
        //await new Promise(resolve => setTimeout(resolve, 10000));
        await cicassService.downloadFilePost(`${apiUrl}/export/daily/${calendarId}/${yyyymmdd}`, {}, "XLSX");
    } finally {
        setIsDownloading(false);
    }
  };

  const handlePrevMonth = () => {
    setSelectedDate((prev) => {
      const prevMonth = new Date(prev);
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      return prevMonth;
    });
  };

  const handleNextMonth = () => {
    setSelectedDate((prev) => {
      const nextMonth = new Date(prev);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      return nextMonth;
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={itLocale}>
      <div style={{ margin: "1.2rem 0 .5rem 1.2rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" component="h2">
            {calendarName || t("Caricamento Calendario...")}
          </Typography>
          <Box>
            <IconButton onClick={handleOpenPrintMenu} color="primary">
                <PrintIcon />
            </IconButton>
            <Popover open={open} anchorEl={anchorEl} onClose={handleClosePrintMenu} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Box p={2} display="flex" flexDirection="column" gap={2}>
                    <Typography variant="subtitle2">Seleziona data per stampa</Typography>
                    <DatePicker
                        label="Data"
                        value={printDate}
                        onChange={(newValue) => setPrintDate(newValue)}
                        renderInput={(params) => <TextField {...params} size="small" />}
                    />
                    <Button variant="contained" 
                            disabled={isDownloading}
                            startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : null}
                            onClick={async () => {  await handleExportExcel(calendarIdFromRoute, printDate); handleClosePrintMenu(); }}>
                        {isDownloading ? "Generazione..." : "Scarica Excel"}
                    </Button>
                </Box>
            </Popover>
        </Box>
        </div>
          {/* DatePicker allineato a destra con padding 5px */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end',  alignItems: "center",p: '5px' }}>
                  {/* PULSANTE MESE PRECEDENTE */}
        <Tooltip title={isPrevDisabled ? "Non puoi andare oltre" : "Mese Precedente"}>
          <span style={{ display: 'inline-flex' }}>
          <IconButton 
            onClick={handlePrevMonth} 
            disabled={isPrevDisabled}
            size="medium"
            sx={{ 
                my: 0.5,
                mr: 1, // ⬅️ AGGIUNTO MARGINE DESTRO
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                    bgcolor: 'primary.dark', // Hover è già corretto
                },
                '&.Mui-disabled': {
                    bgcolor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(255, 255, 255, 0.4)',
                }
            }}
        >
        <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          </span>
        </Tooltip>
        <DatePicker
            views={[ 'year', 'month' ]}
            minDate={minDate}
            maxDate={maxDate}
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                helperText={null}
                sx={{ width: 180 }}
              />
            )}
          />
        {/* PULSANTE MESE SUCCESSIVO */}
        <Tooltip title={isNextDisabled ? "Non puoi andare oltre" : "Mese Successivo"}>
          <span style={{ display: 'inline-flex' }}>
          <IconButton 
            onClick={handleNextMonth} 
            disabled={isNextDisabled} // Disabilita se > maxDate
            size="medium"
            // ⬅️ NUOVE PROPRIETÀ DI STILE
            sx={{ 
                my: 0.5,ml: 1,
                bgcolor: 'primary.main', // Sfondo blu
                color: 'white',         // Icona bianca
                '&:hover': {
                    bgcolor: 'primary.dark', // Effetto hover leggermente più scuro
                },
                // Assicura che l'icona bianca sia visibile anche quando disabilitato
                '&.Mui-disabled': {
                    bgcolor: 'rgba(0, 0, 0, 0.12)', // Colore grigio standard di disabilitazione
                    color: 'rgba(255, 255, 255, 0.4)', // Icona grigia chiara
                }
            }}
        >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
          </span>
        </Tooltip>  
        </Box>

      {/* GRIGLIA MENSILE DEI TURNI */}
      {isReady && (
        <MonthlyShift
          year={year}
          month={month}
          t={t}
          currentAnaCalendarId={calendarIdFromRoute} // 🚀 Passiamo l'ID estratto dinamicamente dalla rotta (es. 6)
          targetEventId={targetEventId}
          onEventAutoOpened={clearTargetEvent}
          setCalendarName={setCalendarName}
        />
      )}
    </LocalizationProvider>
  );
};

export default withTranslation()(DynamicShiftPage);